import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import * as ExcelJS from 'exceljs';

interface BulkFilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
}

interface TableData {
  headers: string[];
  rows: string[][];
}

const BulkFilePreviewModal = ({ isOpen, onClose, file }: BulkFilePreviewModalProps) => {
  const [tableData, setTableData] = useState<TableData>({ headers: [], rows: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const parseCSV = (text: string): TableData => {
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length === 0) throw new Error('File is empty');

    const headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
    const rows = lines
      .slice(1)
      .map((line) => line.split(',').map((cell) => cell.trim().replace(/"/g, '')));

    return { headers, rows };
  };

  const parseExcel = async (file: File): Promise<TableData> => {
    const workbook = new ExcelJS.Workbook();
    const arrayBuffer = await file.arrayBuffer();
    await workbook.xlsx.load(arrayBuffer);

    const worksheet = workbook.getWorksheet(1); // Get first worksheet
    if (!worksheet) throw new Error('No worksheet found in Excel file');

    const headers: string[] = [];
    const rows: string[][] = [];

    worksheet.eachRow((row, rowNumber) => {
      const rowValues = row.values as any[];
      const cleanValues = rowValues
        .slice(1)
        .map((cell) => (cell !== null && cell !== undefined ? String(cell) : ''));

      if (rowNumber === 1) {
        headers.push(...cleanValues);
      } else {
        rows.push(cleanValues);
      }
    });

    if (headers.length === 0) throw new Error('No headers found in Excel file');

    return { headers, rows };
  };

  useEffect(() => {
    if (isOpen && file) {
      setIsLoading(true);
      setError('');
      setTableData({ headers: [], rows: [] });

      const parseFile = async () => {
        try {
          if (file.name.toLowerCase().endsWith('.csv')) {
            const text = await file.text();
            const data = parseCSV(text);
            setTableData(data);
          } else if (file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
            const data = await parseExcel(file);
            setTableData(data);
          } else {
            throw new Error('Unsupported file format. Please use CSV or Excel files.');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to parse file');
          setTableData({ headers: [], rows: [] });
        } finally {
          setIsLoading(false);
        }
      };

      parseFile();
    }
  }, [isOpen, file]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[72px] right-0 bottom-0 z-40 flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-white rounded-xl w-[900px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mx-4 py-4 border-b border-[#621C43]">
          <div>
            <h2 className="text-xl font-bold text-[#621C43]">Bulk File Preview</h2>
            <p className="text-sm text-[#621C43] font-medium">
              {file?.name} - {tableData.rows.length} records found
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#621C43] hover:text-[#4A082D] transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="text-[#621C43]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#621C43] mx-auto mb-2"></div>
                <p className="text-sm">Loading file preview...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-red-800 font-medium mb-2">Error parsing file</h3>
                <p className="text-red-600 text-sm mb-3">{error}</p>
                <div className="text-xs text-red-500">
                  <p className="font-medium mb-2">Supported file formats:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>
                      <strong>CSV files:</strong> Comma-separated values with headers in first row
                    </li>
                    <li>
                      <strong>Excel files (.xlsx, .xls):</strong> First worksheet with headers in
                      first row
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto p-6">
              {tableData.headers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-[#621C43] text-white">
                        {tableData.headers.map((header, index) => (
                          <th
                            key={index}
                            className="border border-gray-300 px-4 py-2 text-left font-medium"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.rows.slice(0, 100).map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                        >
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className="border border-gray-300 px-4 py-2 text-sm"
                            >
                              {cell || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {tableData.rows.length > 100 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800 text-sm">
                        Showing first 100 rows. Total rows: {tableData.rows.length}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#621C43]">No data found in the file</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkFilePreviewModal;
